import { Breadcrumb as BreadcrumbD, BreadcrumbProps, Button, Flex, Modal, Input, List, Grid, Typography } from 'antd';
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb';
import { useEffect, useMemo, useState } from 'react';
import { TypeMenu } from '@/Models/Menu';
import { CompassOutlined, SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import utl from '@/Utils/Utilitario';
import useToken from 'antd/es/theme/useToken';
import { useMenusDisponiveis, useMenuSelecionado } from '@/Contexts/NavegacaoContext';
import { NovasRotas } from './Sistema/Menu/Menu';
import { ItemDoMenu } from './Sistema/Menu/menu-types';

type TypeBreadcrumb = BreadcrumbProps

const obterCadeia = (origem: TypeMenu): TypeMenu[] => {
    let _chain: TypeMenu[] = [origem];
    while (true) {
        const proximo = _chain[_chain.length - 1].menuPai;
        if (proximo === undefined) break;
        _chain.push(proximo);
    }

    _chain.reverse();
    return _chain;
};

export const Breadcrumb = (props: TypeBreadcrumb) => {
    
    const menus = useMenusDisponiveis();
    const { menuSelecionado } = useMenuSelecionado();

    const breakpoint = Grid.useBreakpoint();

    const breadCrumbItems: ItemType[] = useMemo(() => {
        const cadeia = !menuSelecionado ? [] : obterCadeia(menuSelecionado);
        return cadeia.map((m: TypeMenu) => {
            return {
                key: m.codigo,
                title: m.nome,
            };
        });
    }, [menuSelecionado]);

    //#region Busca rápida

    const [opcoesDeBusca, setOpcoesDeBusca] = useState<{ exibir: boolean }>({ exibir: false });
    const [filtro, setFiltro] = useState<string>("");

    const menusNavegaveis = useMemo((): TypeMenu[] => {
        return (menus?.filter((m, idx, arr) => m.rota != undefined && !arr.some((m2) => m2.codPai === m.codigo)) ?? [])
        
        .sort((a, b) => a.nome.localeCompare(b.nome));
    }, [menus]);

    const filtrarMenu = (m: TypeMenu): boolean =>
        m.rota != undefined && (
            m.nome.toLowerCase().includes(filtro.toLowerCase()) || 
            m.rota.toLowerCase().includes(filtro.toLowerCase()) ||
            m.funcionalidades.some((f) => f.descricao?.toLowerCase().includes(filtro.toLowerCase())) ||
            false
        );

    const exibirBuscaRapida = () => {
        setOpcoesDeBusca({ exibir: true });
    }

    const fecharBuscaRapida = () => {
        setOpcoesDeBusca({ exibir: false });
        setFiltro("");
    }

    const dataSource = menusNavegaveis.filter(filtrarMenu);

    //#endregion

    const [ _, token ] = useToken();
    const { colorLinkHover } = token;

    const _filtroDeBuscaId = "input-busca-rapida";
    useEffect(() => {
        if(opcoesDeBusca.exibir) {
            window.setTimeout(() => {
                const input = document.getElementById(_filtroDeBuscaId);
                input?.focus();
            }, 100);
        }
    }, [opcoesDeBusca])

    useMemo(() => {
        const atalho_ctrl_K = (e: KeyboardEvent) => {
            if(opcoesDeBusca.exibir) return;
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpcoesDeBusca({ exibir: true });
            }
        };

        const atalho_esc = (e: KeyboardEvent) => {
            if(opcoesDeBusca.exibir) {
                if (e.key.toLowerCase() === "escape") {
                    e.preventDefault();
                    setOpcoesDeBusca({ exibir: false });
                }
            }
        };

        window.addEventListener("keydown", atalho_ctrl_K);
        window.addEventListener("keydown", atalho_esc);

        return () => {
            window.removeEventListener("keydown", atalho_ctrl_K) 
            window.removeEventListener("keydown", atalho_esc);
        };
    }, []);

    return (
        <Flex justify="space-between" align="center">
            <Modal open={opcoesDeBusca.exibir} onCancel={fecharBuscaRapida} footer={null} title="Buscar menu" width={breakpoint.md ? "60vw" : "80vw"}>
                <Input
                    id={_filtroDeBuscaId}
                    placeholder="Digite para buscar..."
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                    style={{ marginBottom: 16 }}
                    autoComplete="off"
                />
                <List
                    dataSource={dataSource}
                    style={{
                        maxHeight: "50vh",
                        overflow: "auto",
                        scrollbarWidth: "thin",
                    }}
                    renderItem={(item) => {
                        const r = NovasRotas[item.codigo as ItemDoMenu]?.url;
                        return !r ? undefined : (
                            <List.Item
                                actions={[
                                    <Link to={r} onClick={fecharBuscaRapida} tabIndex={-1}>
                                        <CompassOutlined /> Acessar
                                    </Link>,
                                ]}
                            >
                                <List.Item.Meta
                                    title={
                                        <Link
                                            to={r}
                                            onClick={fecharBuscaRapida}
                                            onFocus={(e) => {
                                                e.currentTarget.style.color = colorLinkHover;
                                                e.currentTarget.style.textDecoration = "underline";
                                            }}
                                            onBlur={(e) => (e.currentTarget.style.color = "")}
                                        >
                                            {item.nome}
                                        </Link>
                                    }
                                    description={
                                        <Typography>
                                            <Typography.Text strong type="secondary">
                                                {obterCadeia(item).map((m) => m.nome).join(" / ")}
                                            </Typography.Text>
                                            <br />
                                            <Typography.Text type="secondary">
                                                {utl.capitalizarInicial(item.funcionalidades.map((i) => i.descricao?.toLowerCase()).join(", "))}
                                            </Typography.Text>
                                        </Typography>
                                    }
                                />
                            </List.Item>
                        )
                    }}
                />
            </Modal>

            <BreadcrumbD {...props} items={breadCrumbItems} />
            <Button
                type="primary"
                shape="circle"
                icon={<SearchOutlined style={{ fontSize: 24 }} />}
                title='Busca rápida'
                onClick={exibirBuscaRapida}
                style={{
                    position: "fixed",
                    top: 16,
                    right: 16,
                    zIndex: 10,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    width: "3rem",
                    height: "3rem",
                    fontSize: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
           />
        </Flex>
    );
}
